import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ApiMethod } from 'src/app/decorators/api-method.decorator';
import { BinderClassification, BinderExportFiles } from 'src/app/models/binder/binder-classification.model';
import { BinderContent, BinderTabDocument } from 'src/app/models/binder/binder-content.model';
import { BinderDetails } from 'src/app/models/binder/binder-details.model';
import { BinderEditForm } from 'src/app/models/binder/binder-edit-form.model';
import { BinderEditJob } from 'src/app/models/binder/binder-edit-job.model';
import { BinderInfo } from 'src/app/models/binder/binder-info.model';
import { BinderQRScanHistory } from 'src/app/models/binder/binder-QR-scan-history.model';
import { BinderSetupData } from 'src/app/models/binder/binder-setup-data.model';
import { BinderUserAccessAccount } from 'src/app/models/binder/binder-user-access-account.model';
import { BinderUserPermission } from 'src/app/models/binder/binder-user-permission.model';
import { BinderWizardDetails } from 'src/app/models/binder/binder-wizard-details.model';
import { JobLogbook } from 'src/app/models/my-work/job-logbook.model';
import { environment } from 'src/environments/environment';
import {BinderFilter} from '../../../models/binder/binder-filter.model';
import {BinderDocumentType} from '../../../models/binder/binder-documentType-model';
import {BinderDocumentTypeMeasurement} from '../../../models/binder/binder-documentType-measurement.model';
import { ReadingMeasurement } from 'src/app/models/binder/binder-reading-measurement.model';
import { NormalizationFieldExceptionCriteria } from 'src/app/models/normalization-field-exception-criteria.model';
import { BaseBinderWizardService } from '../../interfaces/base-binder-wizard-service';
import { BinderSessionViewByEmail } from 'src/app/models/binder/binder-session-view.model';

@Injectable({
	providedIn: 'root'
})
export class BinderWizardService implements BaseBinderWizardService {

	private baseUrl = `${environment.baseUrl}/binder`;

	constructor(private httpClient: HttpClient) { }

	@ApiMethod()
	getBinderWizardDetails(token: string): Observable<BinderWizardDetails> {
		return this.httpClient.get<BinderWizardDetails>(`${this.baseUrl}/${token}/wizard`);
	}

	@ApiMethod()
	submitBinderQuery(token: string, data: {email: string, hasWorkToDo: boolean, userAccountId?: number, isChannelPartner: boolean}): Observable<void> {

		return this.httpClient.post<void>(`${this.baseUrl}/${token}/wizard/code`, data);
	}

	@ApiMethod()
	submitCode(token: string, code: string): Observable<{ token: string }> {
		const formData = new FormData();
		formData.append('code', code);

		return this.httpClient.post<{ token: string }>(`${this.baseUrl}/${token}/wizard`, formData);
	}

	@ApiMethod()
	getBinderDetails(token: string): Observable<BinderDetails> {
		return this.httpClient.get<BinderDetails>(`${this.baseUrl}/${token}/viewer`);
	}

	@ApiMethod()
	getBinderYears(token: string): Observable<BinderSetupData> {
		return this.httpClient.get<BinderSetupData>(`${this.baseUrl}/${token}/viewer/years`);
	}

	@ApiMethod()
	getBinderClassificationDocuments(token: string, binderFilter: BinderFilter): Observable<BinderContent> {
		let params = new HttpParams()
			.set('year', binderFilter.year ? binderFilter.year.toString() : '')
			.set('fromDate', binderFilter.fromDate ? binderFilter.fromDate.toISOString() : '')
			.set('toDate', binderFilter.toDate ? binderFilter.toDate.toISOString() : '')
			.set('showReviewRequired', binderFilter.hideReviewRequired ? 'false' : 'true')
			.set('serviceCategoryDocumentReadingId', binderFilter.serviceCategoryDocumentReadingId != null
				? binderFilter.serviceCategoryDocumentReadingId.toString()
				: '')
			.set('readingCriteria', binderFilter.readingCriteria || '')
			.set('includeExpectationTags', binderFilter.includeExpectationTags ? 'true' : 'false')
			.set('expectationAssetTagId', binderFilter.expectationAssetTagId ? binderFilter.expectationAssetTagId.toString() : '');

		if (Array.isArray(binderFilter.readingValue) && binderFilter.readingValue.length > 0) {
			const readingValueStr = JSON.stringify(binderFilter.readingValue);
			params = params.set('readingValue', readingValueStr);
		}
		else if (typeof binderFilter.readingValue === 'string' && binderFilter.readingValue.trim() !== '') {
			params = params.set('readingValue', binderFilter.readingValue);
		}
		else {
			params = params.set('readingValue', '');
		}

		if (binderFilter.documentTypeIds?.length > 0) {
			binderFilter.documentTypeIds.forEach(id => {
				params = params.append('documentTypeIds', id.toString());
			});
		}

		return this.httpClient.get<BinderContent>(`${this.baseUrl}/${token}/viewer/documents`, { params });
	}

	@ApiMethod()
	getBinderDocumentTypes(token: string): Observable<BinderDocumentType[]>{
		return this.httpClient.get<BinderDocumentType[]>(`${this.baseUrl}/${token}/viewer/binderDocumentTypes`);
	}

	@ApiMethod()
	getBinderReadingsMeasurements(token: string, documentTypeIds: number[], commonReadingsOnly: boolean): Observable<ReadingMeasurement[]> {
		let params = new HttpParams();

		documentTypeIds.forEach(id => {
			params = params.append('documentTypeIds', id.toString());
		});

		params = params.append('commonReadingsOnly',  commonReadingsOnly.toString());

		return this.httpClient.get<any>(`${this.baseUrl}/${token}/viewer/binderReadingsMeasurements`, { params });
	}

	@ApiMethod()
	getNormalizationFieldExceptionCriterias(): Observable<NormalizationFieldExceptionCriteria[]>{
		return this.httpClient.get<NormalizationFieldExceptionCriteria[]>(`${this.baseUrl}/normalizationFieldExceptionCriteria`);
	}

	@ApiMethod()
	getJobDocument(token: string, documentId: number): Observable<Blob> {
		return this.httpClient.get<Blob>(`${this.baseUrl}/${token}/viewer/documents/${documentId}`, { responseType: 'blob' as 'json' });
	}

	@ApiMethod()
	trackBinderSessionDocumentView(req: { token: string; urlHashActionTypeId: number; documentId: number; parentTabId?: number; childTabId?: number; }): Observable<void> {
		return of(undefined);
	}

	@ApiMethod()
	getBinderClassification(token: string): Observable<BinderClassification[]> {
		return this.httpClient.get<BinderClassification[]>(`${this.baseUrl}/${token}/viewer/classification`);
	}

	@ApiMethod()
	hasVendorBinderAccess(token: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.baseUrl}/${token}/viewer/has-vendor-access`);
	}

	@ApiMethod()
	hasAddToBinderAccess(token: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.baseUrl}/${token}/viewer/has-add-to-binder-access`);
	}

	@ApiMethod()
	getVirtualBinderDigitalLogbook(token: string): Observable<JobLogbook[]> {
		return this.httpClient.get<JobLogbook[]>(`${this.baseUrl}/${token}/viewer/digital-logbooks`);
	}

	@ApiMethod()
	getVirtualBinderInfo(token: string, digitalLogFormId: number, jobId: number): Observable<BinderInfo> {
		return this.httpClient.get<BinderInfo>(`${this.baseUrl}/${token}/jobs/${jobId}/forms/${digitalLogFormId}`);
	}

	@ApiMethod()
	getVirtualBinderAccessHash(token: string, digitalLogFormId: number): Observable<{binderUri?: string}> {
		return this.httpClient.get<{binderUri?: string}>(`${this.baseUrl}/${token}/forms/${digitalLogFormId}/binder-access`);
	}

	@ApiMethod()
	getBinderTargerEmailAddress(token: string): Observable<{targetEmail: string}> {
		return this.httpClient.get<{targetEmail: string}>(`${this.baseUrl}/${token}/target-email`);
	}

	@ApiMethod()
	submitDocumentation(token: string, formData: {binderClassificationId: number, documents: BinderTabDocument[], groupName: string}[], year: number): Observable<void> {
		return this.httpClient.post<void>(`${this.baseUrl}/${token}/submit-documentation/${year}`, formData);
	}

	@ApiMethod()
	getQRScanHistory(token: string): Observable<BinderQRScanHistory[]> {
		return this.httpClient.get<BinderQRScanHistory[]>(`${this.baseUrl}/${token}/qr-scan-history`);
	}

	@ApiMethod()
	hasAccessToQRScansHistory(token: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.baseUrl}/${token}/can-access-qr-scans`);
	}

	@ApiMethod()
	hasEditBinderAccess(token: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.baseUrl}/${token}/viewer/has-edit-access`);
	}

	@ApiMethod()
	hasWorkToDo(token: string): Observable<JobLogbook> {
		return this.httpClient.get<JobLogbook>(`${this.baseUrl}/${token}/viewer/has-work-to-do`);
	}

	@ApiMethod()
	hasCustomerPermission(token: string, permissionType: number): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.baseUrl}/${token}/has-customer-permission/${permissionType}`);
	}

	@ApiMethod()
	getBinderUserAccess(token: string): Observable<BinderUserPermission> {
		return this.httpClient.get<BinderUserPermission>(`${this.baseUrl}/${token}/viewer/binder-permission`);
	}

	@ApiMethod()
	hasBinderUserPermission(token: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.baseUrl}/${token}/viewer/has-binder-user-permission`);
	}

	@ApiMethod()
	getBinderEditForms(token: string, binderTabId: number): Observable<BinderEditForm[]> {
		return this.httpClient.get<BinderEditForm[]>(`${this.baseUrl}/${token}/forms/tabs/${binderTabId}`);
	}

	@ApiMethod()
	getBinderEditJobs(token: string, formTemplateId: number, year?: number): Observable<BinderEditJob[]> {
		let yearParam = year || '';
		return this.httpClient.get<BinderEditJob[]>(`${this.baseUrl}/${token}/jobs/forms/${formTemplateId}/years/${yearParam}`);
	}

	@ApiMethod()
	getBinderUserAccessData(token: string, email: string): Observable<BinderUserAccessAccount> {
		return this.httpClient.get<BinderUserAccessAccount>(`${this.baseUrl}/${token}/${email}/account`);
	}

	@ApiMethod()
	isBinderAndCustomerLegacy(token: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.baseUrl}/${token}/viewer/is-binder-legacy`);
	}

	@ApiMethod()
	isBinderEmailAllowed(token: string, email: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.baseUrl}/${token}/is-email-allowed`, {
			params: new HttpParams()
			.set('emailAddress', email) });
	}

	@ApiMethod()
	checkAccessToken(hashId, email, token): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.baseUrl}/check-access-token`, { params: { hashId, email, token } });
	}

	@ApiMethod()
	getCountries(): Observable<any[]> {
		return this.httpClient.get<any[]>(`${this.baseUrl}/countries`);
	}

	@ApiMethod()
	getStates(): Observable<any[]> {
		return this.httpClient.get<any[]>(`${this.baseUrl}/states`);
	}

	@ApiMethod()
	getBindersByAddress(addressId): Observable<any[]> {
		return this.httpClient.get<any[]>(`${this.baseUrl}/binders/address/${addressId}`);
	}

	@ApiMethod()
	getSiteAddresses(latitude, longitude): Observable<any[]> {
		return this.httpClient.get<any[]>(`${this.baseUrl}/site-addresses`, { params: { latitude: latitude || '', longitude: longitude || '' } });
	}

	@ApiMethod()
	exportBinderFiles(token: string, binderContent: BinderExportFiles): Observable<void> {
		return this.httpClient.post<void>(`${this.baseUrl}/${token}/export-documents`, binderContent);
	}
	@ApiMethod()
	getBinderSessions(token: string, email: string): Observable<BinderSessionViewByEmail[]> {
		return this.httpClient.get<BinderSessionViewByEmail[]>(`${this.baseUrl}/${token}/${email}/binder-sessions`);
	}
	@ApiMethod()
	submitBinderSessionAccessRequest(
		token: string, email: string, binderSessionId: number
	): Observable<{ redirectUri: string }> {
		return this.httpClient.post<{ redirectUri: string }>(
			`${this.baseUrl}/${token}/${email}/binder-sessions/${binderSessionId}/urlHash`,
			{});
	}
}
